import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Select,
  Text,
} from '@chakra-ui/react';
import { FiSearch, FiUser, FiChevronDown, FiCheck } from 'react-icons/fi';
import { PiStudentFill } from 'react-icons/pi';
import { debounce } from 'throttle-debounce';
import { DATE_FILTER_OPTIONS, ACTION_FILTER_OPTIONS } from 'api/administrador/auditoria';

const SEARCH_MODES = {
  responsable: { label: 'Responsable', icon: FiUser, hint: 'Quien ejecutó la acción' },
  estudiante: { label: 'Estudiante', icon: PiStudentFill, hint: 'A quién afectó la acción' },
};

export default function AuditFilters({ filters, onFilterChange }) {
  const [searchValue, setSearchValue] = useState(filters.search || '');
  const searchMode = filters.searchMode || 'responsable';
  const activeMode = SEARCH_MODES[searchMode] || SEARCH_MODES.responsable;
  const ActiveIcon = activeMode.icon;

  const debouncedSearch = React.useMemo(
    () =>
      debounce(500, (value) => {
        onFilterChange({ ...filters, search: value });
      }),
    [filters, onFilterChange]
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedSearch(value);
  };

  const handleModeChange = (newMode) => {
    if (newMode === searchMode) return;
    debouncedSearch.cancel();
    setSearchValue('');
    onFilterChange({ ...filters, searchMode: newMode, search: '' });
  };

  const handleDateChange = (e) => {
    const value = e.target.value;
    let dateFrom = null;
    let dateTo = null;

    if (value && value !== 'all') {
      const days = parseInt(value);
      const now = new Date();
      dateFrom = new Date(now.getTime() - days * 24 * 60 * 60 * 1000).toISOString();
      dateTo = new Date().toISOString();
    }

    onFilterChange({ ...filters, dateRange: value, dateFrom, dateTo });
  };

  const handleActionChange = (e) => {
    onFilterChange({ ...filters, action: e.target.value });
  };

  return (
    <Flex gap={4} flexWrap="wrap" p={6} borderBottom="1px solid" borderColor="gray.100">
      <Box flex="1" minW="280px">
        <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={2} textTransform="uppercase">
          Búsqueda
        </Text>
        <Flex
          align="stretch"
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          overflow="hidden"
          _focusWithin={{ borderColor: '#574feb', boxShadow: '0 0 0 1px #574feb' }}
        >
          <Menu placement="bottom-start" matchWidth={false}>
            <MenuButton
              as="button"
              type="button"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '0 12px',
                background: '#f4f3ff',
                borderRight: '1px solid #e2e8f0',
                color: '#4338ca',
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              <Flex align="center" gap={2}>
                <ActiveIcon size={14} />
                <Text as="span" fontSize="13px" fontWeight="500">
                  {activeMode.label}
                </Text>
                <FiChevronDown size={14} style={{ opacity: 0.7 }} />
              </Flex>
            </MenuButton>
            <MenuList minW="240px" py={1}>
              <Text
                px={4}
                pt={2}
                pb={1}
                fontSize="10px"
                fontWeight="semibold"
                color="gray.500"
                textTransform="uppercase"
                letterSpacing="wider"
              >
                Filtrar por
              </Text>
              {Object.entries(SEARCH_MODES).map(([key, mode]) => {
                const ModeIcon = mode.icon;
                const isActive = key === searchMode;
                return (
                  <MenuItem
                    key={key}
                    onClick={() => handleModeChange(key)}
                    bg={isActive ? '#f4f3ff' : 'transparent'}
                    _hover={{ bg: isActive ? '#f4f3ff' : 'gray.50' }}
                    px={4}
                    py={2}
                  >
                    <Flex align="center" gap={3} w="100%">
                      <ModeIcon size={16} color={isActive ? '#4338ca' : '#5f5e5a'} />
                      <Box flex="1">
                        <Text fontSize="13px" fontWeight="500" color="gray.800">
                          {mode.label}
                        </Text>
                        <Text fontSize="11px" color="gray.500">
                          {mode.hint}
                        </Text>
                      </Box>
                      {isActive && <FiCheck size={16} color="#4338ca" />}
                    </Flex>
                  </MenuItem>
                );
              })}
            </MenuList>
          </Menu>

          <InputGroup flex="1">
            <InputLeftElement pointerEvents="none">
              <FiSearch color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Buscar por ID, Nombre o Detalle..."
              value={searchValue}
              onChange={handleSearchChange}
              border="none"
              borderRadius="0"
              _focus={{ boxShadow: 'none', border: 'none' }}
              _hover={{ border: 'none' }}
            />
          </InputGroup>
        </Flex>
      </Box>

      <Box minW="180px">
        <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={2} textTransform="uppercase">
          Fecha
        </Text>
        <Select
          value={filters.dateRange || '30'}
          onChange={handleDateChange}
          bg="white"
          borderColor="gray.200"
          _hover={{ borderColor: 'gray.300' }}
          _focus={{ borderColor: '#574feb', boxShadow: '0 0 0 1px #574feb' }}
        >
          {DATE_FILTER_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      </Box>

      <Box minW="180px">
        <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={2} textTransform="uppercase">
          Acción
        </Text>
        <Select
          value={filters.action || ''}
          onChange={handleActionChange}
          bg="white"
          borderColor="gray.200"
          _hover={{ borderColor: 'gray.300' }}
          _focus={{ borderColor: '#574feb', boxShadow: '0 0 0 1px #574feb' }}
        >
          {ACTION_FILTER_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      </Box>
    </Flex>
  );
}
